import React, { useState } from 'react';
import { toast } from 'react-toastify';
import * as XLSX from '@e965/xlsx';
import { DataLoaderProps, BuyerEntry } from '../types';

interface ExcelRow {
    [key: string]: string | number | null;
}

/**
 * Computes the SHA-256 hash of an ArrayBuffer.
 * Uses the Web Crypto API which is available in modern browsers.
 * 
 * @param data - The ArrayBuffer to compute the hash for
 * @returns A promise that resolves to a 64-character lowercase hexadecimal string representing the SHA-256 hash
 */
export async function computeSha256Hash(data: ArrayBuffer): Promise<string> {
    // Ensure we pass a proper typed array to crypto.subtle.digest for better compatibility
    const uint8Array = new Uint8Array(data);
    const hashBuffer = await crypto.subtle.digest('SHA-256', uint8Array);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
}

/**
 * Transforms an Excel row into a standardized BuyerEntry format.
 * - ID: from 'ID' field
 * - email: from 'E-Mail' field
 * - name: concatenation of 'Vorname' and 'Nachname'
 * - ticketsPurchased: from 'Anzahl zu kaufender Lose' field
 */
export function transformExcelRowToBuyerEntry(row: ExcelRow): BuyerEntry {
    const id = String(row['ID'] ?? '');
    const email = String(row['E-Mail'] ?? '');
    const vorname = String(row['Vorname'] ?? '').trim();
    const nachname = String(row['Nachname'] ?? '').trim();
    const name = [vorname, nachname].filter(Boolean).join(' ');
    const ticketsPurchased = Number(row['Anzahl zu kaufender Lose']) || Number(row['Anzahl Tickets']) ||  0;

    return {
        id,
        email,
        name,
        ticketsPurchased,
    };
}

/**
 * Transforms an array of Excel rows into standardized BuyerEntry format.
 */
export function transformExcelDataToBuyerEntries(data: ExcelRow[]): BuyerEntry[] {
    return data.map(transformExcelRowToBuyerEntry);
}

const ExcelLoader: React.FC<DataLoaderProps> = ({ onDataLoaded, onBuyerDataLoaded, onRandomnessSeedLoaded }) => {
    const [excelData, setExcelData] = useState<ExcelRow[]>([]);
    const [columns, setColumns] = useState<string[]>([]);
    const hiddenColumns = new Set([
        'start time',
        'completion time',
        'id',
        'email',
        'e-mail',
        'last modified time',
        'iban',
        'bic',
        'sepa mandat',
        'name'
    ]);
    const displayColumns = columns.filter(c => !hiddenColumns.has(c.toLowerCase()));
    const [buyerEntries, setBuyerEntries] = useState<BuyerEntry[]>([]);

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;
        
        const reader = new FileReader();
        reader.onload = async e => {
            try {
                const arrayBuffer = e.target?.result as ArrayBuffer;
                const data = new Uint8Array(arrayBuffer);
                const workbook = XLSX.read(data, { type: 'array' });
                const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
                const jsonData = XLSX.utils.sheet_to_json<ExcelRow>(firstSheet, { defval: null });
                
                if (jsonData.length === 0) {
                    toast.error('Die Datei enthält keine Daten');
                    return;
                }
                
                const cols = Object.keys(jsonData[0]);
                setColumns(cols);
                setExcelData(jsonData);

                // Transform to standardized BuyerEntry format
                const transformedData = transformExcelDataToBuyerEntries(jsonData);
                setBuyerEntries(transformedData);

                // Compute SHA-256 hash of the file as randomness seed
                try {
                    const seed = await computeSha256Hash(arrayBuffer);
                    if (onRandomnessSeedLoaded) {
                        onRandomnessSeedLoaded(seed);
                    }
                } catch (hashError) {
                    console.error('Error computing randomness seed:', hashError);
                    // Continue without seed - not critical for file loading
                }

                // Notify parent components
                onDataLoaded(true);
                if (onBuyerDataLoaded) {
                    onBuyerDataLoaded(transformedData);
                }
                
                toast.success(`${jsonData.length} Zeilen geladen`);
            } catch (error) {
                console.error('Error reading file:', error);
                toast.error('Fehler beim Lesen der Datei');
                onDataLoaded(false);
            }
        };
        reader.readAsArrayBuffer(file);
    };

    return (
        <div>
            <div style={{ marginTop: '20px', marginBottom: '30px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                <h3 style={{ marginTop: 0, marginBottom: '16px', fontSize: '18px', color: '#495057' }}>
                    Excel-Datei hochladen
                </h3>
                
                <div style={{ marginBottom: '16px' }}>
                    <label htmlFor="excel-upload" style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                        Datei auswählen:
                    </label>
                    <input
                        id="excel-upload"
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        onChange={handleFileUpload}
                        style={{
                            padding: '8px',
                            fontSize: '14px',
                            border: '1px solid #ced4da',
                            borderRadius: '4px',
                            backgroundColor: 'white'
                        }}
                    />
                </div>

                {excelData.length > 0 && (
                    <div style={{ 
                        marginTop: '16px', 
                        padding: '12px', 
                        backgroundColor: '#d4edda', 
                        border: '1px solid #c3e6cb',
                        borderRadius: '4px',
                        color: '#155724'
                    }}>
                        <strong>Geladen:</strong> {excelData.length} Zeilen mit {displayColumns.length} sichtbaren Spalten
                    </div>
                )}
            </div>

            {buyerEntries.length > 0 && (
                <div style={{ overflowX: 'auto' }}>
                    <h4 style={{ marginBottom: '12px' }}>Transformierte Käuferdaten (erste 5 Zeilen):</h4>
                    <table style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        fontSize: '14px'
                    }}>
                        <thead>
                            <tr style={{ backgroundColor: '#f8f9fa' }}>
                                <th style={{ 
                                    padding: '12px 8px', 
                                    borderBottom: '2px solid #dee2e6',
                                    textAlign: 'left',
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap'
                                }}>
                                    #
                                </th>
                                <th style={{ 
                                    padding: '12px 8px', 
                                    borderBottom: '2px solid #dee2e6',
                                    textAlign: 'left',
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap'
                                }}>
                                    ID
                                </th>
                                <th style={{ 
                                    padding: '12px 8px', 
                                    borderBottom: '2px solid #dee2e6',
                                    textAlign: 'left',
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap'
                                }}>
                                    E-Mail
                                </th>
                                <th style={{ 
                                    padding: '12px 8px', 
                                    borderBottom: '2px solid #dee2e6',
                                    textAlign: 'left',
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap'
                                }}>
                                    Name
                                </th>
                                <th style={{ 
                                    padding: '12px 8px', 
                                    borderBottom: '2px solid #dee2e6',
                                    textAlign: 'left',
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap'
                                }}>
                                    Gekaufte Tickets
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {buyerEntries.slice(0, 5).map((entry, index) => (
                                <tr key={index} style={{
                                    backgroundColor: index % 2 === 0 ? 'white' : '#f8f9fa'
                                }}>
                                    <td style={{ 
                                        padding: '10px 8px', 
                                        borderBottom: '1px solid #dee2e6',
                                        color: '#6c757d'
                                    }}>
                                        {index + 1}
                                    </td>
                                    <td style={{ 
                                        padding: '10px 8px', 
                                        borderBottom: '1px solid #dee2e6',
                                        maxWidth: '200px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                    }}>
                                        {entry.id || '—'}
                                    </td>
                                    <td style={{ 
                                        padding: '10px 8px', 
                                        borderBottom: '1px solid #dee2e6',
                                        maxWidth: '250px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                    }}>
                                        {entry.email || '—'}
                                    </td>
                                    <td style={{ 
                                        padding: '10px 8px', 
                                        borderBottom: '1px solid #dee2e6',
                                        maxWidth: '200px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                    }}>
                                        {entry.name || '—'}
                                    </td>
                                    <td style={{ 
                                        padding: '10px 8px', 
                                        borderBottom: '1px solid #dee2e6'
                                    }}>
                                        {entry.ticketsPurchased}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <p style={{ marginTop: '8px', color: '#6c757d', fontSize: '14px' }}>
                        Gesamt: {buyerEntries.length} Käufer
                    </p>
                </div>
            )}
        </div>
    );
};

export default ExcelLoader;
